import { useContext, useEffect, useRef, useState } from "react";
import { OutorgaContext } from "../../contexts/Outorga";
import ScheduleSelectionController from "../../utils/outorga/cronograma/scheduleSelectionController";
import helper from "../../utils/helper";

const createInitialMonthConfigs = () => {
    const configs = {};

    for (let i = 0; i < 12; i++) {
        const month = Number(i + 1);

        configs[month] = {
            dias_trabalhados: [],
            dias_paridade: false,
            todos_dias: false
        };
    }

    return configs;
};

export const useSchedule = ({ grantId, permissions, isMasterOrAdmin }) => {
    const context = useContext(OutorgaContext);

    const [selectedMonth, setSelectedMonth] = useState(new Date(2024, 0, 1));
    const [monthConfigs, setMonthConfigs] = useState(createInitialMonthConfigs);
    const [selectedDays, setSelectedDays] = useState([]);
    const [globalEvenOddEnabled, setGlobalEvenOddEnabled] = useState(false);
    const [globalEvenOddType, setGlobalEvenOddType] = useState("par");
    const [onlyEvenOdd, setOnlyEvenOdd] = useState(false);
    const [evenOddType, setEvenOddType] = useState("par");
    const [defaultSchedule, setDefaultSchedule] = useState({ start: "00:00", end: "23:59" });

    const getDaysInMonth = (date) => {
        const year = 2024;
        const month = date.getMonth();
        return new Date(year, month + 1, 0).getDate();
    };

    const controllerRef = useRef(
        new ScheduleSelectionController({
            totalDays: getDaysInMonth(selectedMonth),
            isDayDisabled: (day) => isDayDisabled(day)
        })
    );

    const controller = controllerRef.current;

    const generateDaysArray = () => {
        const totalDays = getDaysInMonth(selectedMonth);
        return Array.from({ length: totalDays }, (_, i) => i + 1);
    };

    const generateEvenOddDays = (type) => {
        return generateDaysArray().filter(day => type === "par" ? day % 2 === 0 : day % 2 === 1);
    };

    const getMonthKey = (date) => Number(date.getMonth() + 1);

    const isDayDisabled = (day) => {
        const config = monthConfigs[getMonthKey(selectedMonth)];

        const type = config?.dias_paridade;
        if (!type) return false;

        if (type === "par") return day % 2 !== 0;
        if (type === "impar") return day % 2 !== 1;

        return false;
    };

    const syncController = (days) => {
        controller.setSelectedDays(days);
        setSelectedDays(days);
    };

    const areAllDaysSelected = () => controller.isAllSelected();

    const saveMonthConfig = () => {
        const key = getMonthKey(selectedMonth);

        setMonthConfigs(prev => ({
            ...prev,
            [key]: {
                dias_trabalhados: selectedDays,
                dias_paridade: onlyEvenOdd ? evenOddType : false,
                todos_dias: areAllDaysSelected()
            }
        }));
    };

    useEffect(() => {
        saveMonthConfig();
    }, [selectedDays, onlyEvenOdd, evenOddType]);

    useEffect(() => {
        const payload = buildPayload();
        context.setUnsavedSchedule(payload);
    }, [monthConfigs, defaultSchedule]);

    const handleChangeMonth = (date) => {
        saveMonthConfig();

        const key = getMonthKey(date);
        const savedConfig = monthConfigs[key];

        setSelectedMonth(date);

        const totalDays = getDaysInMonth(date);
        controller.setTotalDays(totalDays);

        if (globalEvenOddEnabled) {
            const totalDays = getDaysInMonth(date);

            const days = Array.from({ length: totalDays }, (_, i) => i + 1)
                .filter(day =>
                    globalEvenOddType === "par"
                        ? day % 2 === 0
                        : day % 2 === 1
                );

            controller.setTotalDays(totalDays);
            controller.setSelectedDays(days);

            setSelectedDays(days);
            setOnlyEvenOdd(true);
            setEvenOddType(globalEvenOddType);

            return;
        }

        if (savedConfig) {
            const dias = savedConfig.dias_trabalhados || [];
            const paridade = savedConfig.dias_paridade;

            setOnlyEvenOdd(!!paridade);
            setEvenOddType(paridade || "par");

            controller.setSelectedDays(dias);
            setSelectedDays(dias);
            return
        }
        controller.clear();
        setSelectedDays([]);

        setOnlyEvenOdd(false);
        setEvenOddType("par");
    };

    const toggleEvenOdd = () => {
        const newValue = !onlyEvenOdd;
        setOnlyEvenOdd(newValue);
        if (onlyEvenOdd && globalEvenOddEnabled) {
            setGlobalEvenOddEnabled(false)
        }

        if (newValue) {
            const days = generateEvenOddDays(evenOddType);
            syncController(days);
            return
        }
        controller.clear();
        syncController([]);
    };

    const toggleEvenOddInAllMonths = () => {
        const isActive = areAllMonthsEvenOdd();
        if (!isActive) {
            setGlobalEvenOddEnabled(true);
        }

        if (isActive) {
            const resetConfigs = {};

            for (let i = 1; i <= 12; i++) {
                resetConfigs[i] = {
                    dias_trabalhados: [],
                    dias_paridade: false,
                    todos_dias: false
                };
            }

            setMonthConfigs(resetConfigs);

            controller.clear();
            setSelectedDays([]);
            setOnlyEvenOdd(false);
            setEvenOddType("par");

            return;
        }

        applyEvenOddToAllMonths(globalEvenOddType);
    };

    const changeEvenOddType = (option) => {
        const type = option.value;
        setEvenOddType(type);

        if (onlyEvenOdd) {
            const days = generateEvenOddDays(type);
            syncController(days);
        }
    };

    const changeEvenOddTypeInAllMonths = (option) => {
        const type = option.value;
        setGlobalEvenOddType(type);

        applyEvenOddToAllMonths(type);
    };

    const applyEvenOddToAllMonths = (type) => {
        const newConfigs = {};

        for (let i = 1; i <= 12; i++) {
            const totalDays = new Date(2024, i, 0).getDate();

            const days = Array.from({ length: totalDays }, (_, d) => d + 1)
                .filter(day => type === "par" ? day % 2 === 0 : day % 2 === 1);

            newConfigs[i] = {
                dias_trabalhados: days,
                dias_paridade: type,
                todos_dias: false
            };
        }

        setMonthConfigs(newConfigs);

        const currentMonth = getMonthKey(selectedMonth);
        const currentDays = newConfigs[currentMonth].dias_trabalhados;

        controller.setSelectedDays(currentDays);
        setSelectedDays(currentDays);

        setOnlyEvenOdd(true);
        setEvenOddType(type);
    };

    const toggleAllDays = () => {
        if (controller.isAllSelected()) {
            syncController(controller.clear());
            return
        }
        syncController(controller.selectAll());
    };

    const toggleAllDaysInAllMonths = () => {
        const isAllSelectedGlobally = Object.values(monthConfigs).every(
            (month) => month.todos_dias
        );

        if (isAllSelectedGlobally) {
            const resetConfigs = {};

            for (let i = 1; i <= 12; i++) {
                resetConfigs[i] = {
                    dias_trabalhados: [],
                    dias_paridade: false,
                    todos_dias: false
                };
            }

            setMonthConfigs(resetConfigs);

            controller.clear();
            setSelectedDays([]);
            setGlobalEvenOddEnabled(false);
            setGlobalEvenOddType("par");
            setOnlyEvenOdd(false);
            setEvenOddType("par");

            return;
        }

        const newConfigs = {};

        for (let i = 1; i <= 12; i++) {
            const totalDays = new Date(2024, i, 0).getDate();

            const allDays = Array.from({ length: totalDays }, (_, day) => day + 1);

            newConfigs[i] = {
                dias_trabalhados: allDays,
                dias_paridade: false,
                todos_dias: true
            };
        }

        setMonthConfigs(newConfigs);

        const currentMonth = getMonthKey(selectedMonth);
        const currentDays = newConfigs[currentMonth].dias_trabalhados;

        controller.setTotalDays(currentDays.length);
        controller.setSelectedDays(currentDays);

        setSelectedDays(currentDays);

        setGlobalEvenOddEnabled(false);
        setGlobalEvenOddType("par");
        setOnlyEvenOdd(false);
        setEvenOddType("par");
    };

    const areAllMonthsEvenOdd = () => {
        const values = Object.values(monthConfigs);

        if (!values.length) return false;

        const first = values[0].dias_paridade;

        if (!first) return false;

        return values.every(m => m.dias_paridade === first);
    };

    const areAllDaysInAllMonthsSelected = () => {
        return Object.values(monthConfigs).every(
            (month) => month.todos_dias
        );
    };

    const handleMouseDown = (day) => {
        setSelectedDays(controller.handleMouseDown(day));
    };

    const handleMouseEnter = (day) => {
        setSelectedDays(controller.handleMouseEnter(day));
    };

    const handleMouseUp = () => controller.handleMouseUp();

    const loadFromContext = () => {
        const schedule = context?.getGrantSchedule?.();
        if (!schedule) return;

        const { meses = {}, horas_periodo } = schedule;

        setMonthConfigs(prev => {
            const updated = { ...prev };

            Object.entries(meses).forEach(([month, config]) => {
                updated[month] = {
                    dias_trabalhados: config.dias_trabalhados || [],
                    dias_paridade: config.dias_paridade || false,
                    todos_dias: config.todos_dias || false
                };
            });

            return updated;
        });

        const currentKey = getMonthKey(selectedMonth);
        const current = meses[currentKey];

        if (current) {
            const dias = current.dias_trabalhados || [];
            const paridade = current.dias_paridade;

            controller.setSelectedDays(dias);

            setSelectedDays(dias);
            setOnlyEvenOdd(!!paridade);
            setEvenOddType(paridade || "par");
        }

        setDefaultSchedule({
            start: horas_periodo?.inicio || "00:00",
            end: horas_periodo?.fim || "23:59"
        });
    };

    const buildPayload = () => {
        return {
            meses: monthConfigs,
            horas_periodo: {
                inicio: defaultSchedule.start,
                fim: defaultSchedule.end
            }
        };
    };

    const handleSave = () => {
        if (!defaultSchedule.start || !defaultSchedule.end) {
            helper.dispatchEvent("showAviso", { message: "Período de trabalho não preenchido." });
            return;
        }

        context.handleSaveGrantSchedule(grantId, buildPayload());
    };

    useEffect(() => {
        context.getGrant(grantId);
        loadFromContext();
    }, []);

    useEffect(() => {
        controller.setIsDayDisabled(isDayDisabled);
    }, [onlyEvenOdd, evenOddType]);

    const isLoading = context.isLoadingGrantSchedule;
    const canEdit = isMasterOrAdmin || permissions?.patch || permissions?.post;

    return {
        selectedMonth,
        selectedDays,
        globalEvenOddEnabled,
        globalEvenOddType,
        onlyEvenOdd,
        evenOddType,
        defaultSchedule,
        controller,
        handleChangeMonth,
        toggleEvenOdd,
        toggleEvenOddInAllMonths,
        changeEvenOddType,
        changeEvenOddTypeInAllMonths,
        toggleAllDays,
        toggleAllDaysInAllMonths,
        handleMouseDown,
        handleMouseEnter,
        handleMouseUp,
        setDefaultSchedule,
        isDayDisabled,
        areAllMonthsEvenOdd,
        areAllDaysSelected,
        areAllDaysInAllMonthsSelected,
        handleSave,
        generateDaysArray,
        isLoading,
        canEdit
    };
};